import { theme } from 'ant-design-vue'

/**
 * Ant Design theme configuration based on custom base components
 * Colors and styles extracted from:
 * - InputBaseAntd.vue
 * - SelectBaseAntd.vue
 * - CheckboxBaseAntd.vue
 * - RadioBaseAntd.vue
 * - InputNumberBaseAntd.vue
 * - SliderBaseAntd.vue
 * - SwitchBaseAntd.vue
 * - TabsBaseAntd.vue
 * - InputSearchBaseAntd.vue
 * - DatePickerBaseAntd.vue
 * - DateRange.vue (RangePicker)
 * - SegmentBaseAntd.vue
 */
export const antdThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    // Colors - Text
    colorText: 'var(--sds-color-text-default-primary, #ffffff)',
    colorTextSecondary: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
    colorTextTertiary: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
    colorTextQuaternary: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
    colorTextDisabled: 'var(--sds-color-text-default-tertiary, #a6a6a6)',

    // Colors - Border
    colorBorder: 'var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2))',
    colorBorderSecondary: 'var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2))',
    colorPrimary: 'var(--sds-color-border-default-tertiary, #d8dae2)',
    colorPrimaryHover: 'var(--sds-color-border-default-tertiary, #d8dae2)',
    colorPrimaryActive: 'var(--sds-color-border-default-tertiary, #d8dae2)',

    // Colors - Background
    colorBgContainer: 'transparent',
    colorBgElevated: 'rgba(26, 26, 26, 0.95)',
    colorBgLayout: 'transparent',
    colorBgMask: 'rgba(0, 0, 0, 0.45)',
    colorBgSpotlight: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.05))',
    colorFill: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.05))',
    colorFillSecondary: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.1))',
    colorFillTertiary: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.15))',
    colorBgContainerDisabled: 'var(--sds-color-background-disabled-default, rgba(255, 255, 255, 0.02))',

    // Control items
    controlItemBgActive: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.1))',
    controlItemBgActiveHover: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.15))',
    controlItemBgHover: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.1))',

    // Select options
    optionSelectedBg: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.15))',
    optionActiveBg: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.1))',

    // Typography
    fontFamily: "var(--sds-typography-family-poppins, 'Poppins', sans-serif)",
    fontSize: 14,
    fontSizeLG: 16,
    fontSizeSM: 12,
    lineHeight: 1.5,

    // Border radius
    borderRadius: 0,
    borderRadiusLG: 0,
    borderRadiusSM: 0,

    // Disabled
    opacityLoading: 0.5,
  },
  components: {
    Input: {
      colorBgContainer: 'transparent',
      colorBorder: 'var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2))',
      colorBorderHover: 'var(--sds-color-border-default-tertiary)',
      activeBorderColor: 'var(--sds-color-border-default-tertiary)',
      hoverBorderColor: 'var(--sds-color-border-default-tertiary)',
      colorText: 'var(--sds-color-text-default-primary, #ffffff)',
      colorTextPlaceholder: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
      colorTextDisabled: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
      colorBgContainerDisabled: 'var(--sds-color-background-disabled-default, rgba(255, 255, 255, 0.02))',
      borderRadius: 0,
      paddingBlock: 4,
      paddingInline: 12,
      paddingBlockLG: 0,
      paddingInlineLG: 12,
      paddingBlockSM: 0,
      paddingInlineSM: 8,
      fontSize: 14,
      fontSizeLG: 16,
      fontSizeSM: 12,
      controlHeight: 42,
      controlHeightLG: 50,
      controlHeightSM: 34,
    },
    InputNumber: {
      colorBgContainer: 'transparent',
      colorBorder: 'var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2))',
      colorBorderHover: 'var(--sds-color-border-default-tertiary)',
      activeBorderColor: 'var(--sds-color-border-default-tertiary)',
      hoverBorderColor: 'var(--sds-color-border-default-tertiary)',
      colorText: 'var(--sds-color-text-default-primary, #ffffff)',
      colorTextPlaceholder: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
      borderRadius: 0,
      controlHeight: 42,
      controlHeightLG: 50,
      controlHeightSM: 34,
    },
    Select: {
      colorBgContainer: 'transparent',
      colorBorder: 'var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2))',
      colorBorderHover: 'var(--sds-color-border-default-tertiary)',
      colorText: 'var(--sds-color-text-default-primary, #ffffff)',
      colorTextPlaceholder: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
      optionSelectedBg: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.15))',
      optionActiveBg: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.1))',
      optionSelectedColor: 'var(--sds-color-text-default-primary, #ffffff)',
      colorBgElevated: 'rgba(26, 26, 26, 0.95)',
      borderRadius: 0,
      paddingBlock: 0,
      paddingInline: 12,
      controlHeight: 42,
      controlHeightLG: 50,
      controlHeightSM: 34,
      fontSize: 14,
      fontSizeLG: 16,
      fontSizeSM: 12,
    },
    Checkbox: {
      colorPrimary: 'var(--sds-color-icon-brand-green, #00ff88)',
      colorPrimaryHover: 'var(--sds-color-icon-brand-green, #00ff88)',
      colorBgContainer: 'var(--sds-color-background-opacity-200, rgba(255, 255, 255, 0.1))',
      colorBorder: 'var(--sds-color-border-default-tertiary, rgba(255, 255, 255, 0.3))',
      colorBorderHover: 'var(--sds-color-border-default-tertiary)',
      borderRadius: 0,
      size: 16,
    },
    Radio: {
      colorPrimary: 'var(--sds-color-icon-brand-green, #00ff88)',
      colorPrimaryHover: 'var(--sds-color-icon-brand-green, #00ff88)',
      colorBgContainer: 'var(--sds-color-background-opacity-200, rgba(255, 255, 255, 0.1))',
      colorBorder: 'var(--sds-color-border-default-tertiary, rgba(255, 255, 255, 0.3))',
      colorBorderHover: 'var(--sds-color-border-default-tertiary)',
      size: 12,
    },
    Switch: {
      colorPrimary: 'var(--sds-color-icon-brand-green, #00ff88)',
      colorPrimaryHover: 'var(--sds-color-icon-brand-green, #00ff88)',
      colorTextQuaternary: 'var(--sds-color-icon-secondary-tertiary)',
      colorTextTertiary: 'var(--sds-color-border-brand-green)',
    },
    Slider: {
      colorPrimary: 'var(--sds-color-border-brand-default, #d8dae2)',
      colorPrimaryBorder: 'var(--sds-color-border-brand-default, #d8dae2)',
      trackBg: 'var(--sds-color-border-default-secondary, rgba(255, 255, 255, 0.2))',
      railBg: 'var(--sds-color-border-default-secondary, rgba(255, 255, 255, 0.2))',
      handleColor: '#ffffff',
      handleSize: 16,
      handleSizeHover: 16,
      trackHoverBg: 'var(--sds-color-border-brand-default, #d8dae2)',
      dotBorderColor: 'var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2))',
      dotActiveBorderColor: 'var(--sds-color-border-brand-default, #d8dae2)',
    },
    Tabs: {
      colorPrimary: 'var(--sds-color-background-brand-default, #00ff88)',
      colorText: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
      colorTextHeading: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
      colorTextActive: 'var(--sds-color-text-default-primary, #ffffff)',
      colorTextHover: 'var(--sds-color-text-default-primary, #ffffff)',
      itemActiveColor: 'var(--sds-color-text-default-primary, #ffffff)',
      itemHoverColor: 'var(--sds-color-text-default-primary, #ffffff)',
      itemSelectedColor: 'var(--sds-color-text-default-primary, #ffffff)',
      inkBarColor: 'var(--sds-color-background-brand-default, #00ff88)',
      cardBg: 'transparent',
      itemColor: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
    },
    Tooltip: {
      colorBgSpotlight: 'rgba(26, 26, 26, 0.95)',
      colorTextLightSolid: 'var(--sds-color-text-default-primary, #ffffff)',
      borderRadius: 0,
      colorBorder: 'var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2))',
    },
    DatePicker: {
      colorBgContainer: 'transparent',
      colorBorder: 'var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2))',
      colorBorderHover: 'var(--sds-color-border-default-tertiary)',
      activeBorderColor: 'var(--sds-color-border-default-tertiary)',
      hoverBorderColor: 'var(--sds-color-border-default-tertiary)',
      colorText: 'var(--sds-color-text-default-primary, #ffffff)',
      colorTextPlaceholder: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
      colorTextDisabled: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
      colorBgContainerDisabled: 'var(--sds-color-background-disabled-default, rgba(255, 255, 255, 0.02))',
      colorBgElevated: 'rgba(26, 26, 26, 0.95)',
      borderRadius: 0,
      paddingBlock: 4,
      paddingInline: 12,
      paddingBlockLG: 0,
      paddingInlineLG: 12,
      paddingBlockSM: 0,
      paddingInlineSM: 8,
      controlHeight: 42,
      controlHeightLG: 50,
      controlHeightSM: 34,
      fontSize: 14,
      fontSizeLG: 16,
      fontSizeSM: 12,
      // Calendar dropdown styles
      cellActiveWithRangeBg: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.1))',
      cellHoverWithRangeBg: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.05))',
      cellRangeBorderColor: 'var(--sds-color-border-brand-green, #00ff88)',
      timeColumnBg: 'var(--sds-color-background-surface-primary)',
    },
    // RangePicker uses the same config as DatePicker
    // All DatePicker styles apply to RangePicker as well
    Segmented: {
      itemSelectedBg: 'var(--sds-color-background-opacity-200)',
      itemSelectedColor: 'var(--sds-color-text-default-primary, #ffffff)',
      itemColor: 'var(--sds-color-text-default-tertiary, #a6a6a6)',
      itemHoverColor: 'var(--sds-color-text-default-primary, #ffffff)',
      itemHoverBg: 'var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.05))',
      trackBg: 'var(--sds-color-background-opacity-100)',
      trackPadding: 2,
      borderRadius: 4,
      fontSize: 14,
      fontSizeSM: 12,
      fontSizeLG: 16,
      controlHeight: 32,
      controlHeightSM: 28,
      controlHeightLG: 40,
    },
  },
}
